import React, { Suspense, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { FC, Dispatch } from "react";

import * as Clipboard from "expo-clipboard";
import { Box, Heading, VStack, HStack, Button, Text, useTheme, useToast } from "native-base";
import { useWindowDimensions } from "react-native";
import {
  useQueryLoader,
  usePreloadedQuery,
  usePaginationFragment,
  useRelayEnvironment,
  useMutation,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import { fetchQuery } from "relay-runtime";
import type { Subscription } from "relay-runtime";

import WaitingListMutationProgressIndicators from "pianofunclub-crm/components/Buttons/WaitingListMutationProgressIndicators";
import WaitingListTableRow from "pianofunclub-crm/components/ListItems/WaitingListTableRow";
import type { WaitingListTableRowProps } from "pianofunclub-crm/components/ListItems/WaitingListTableRow";
import EnrolmentStatusActionsheet from "pianofunclub-crm/components/Modals/EnrolmentStatusActionsheet";
import type { EnrolmentStatusActionsheetProps } from "pianofunclub-crm/components/Modals/EnrolmentStatusActionsheet";
import type {
  WaitingListStackNavigatorProps,
  WaitingListStackRouteProps,
} from "pianofunclub-crm/navigation/WaitingListNavigator";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import Row from "pianofunclub-shared/components/Base/Row";
import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import CustomFlatListSpinner from "pianofunclub-shared/components/Other/CustomFlatListSpinner";
import {
  CopyIcon,
  RestartIcon,
  UploadIcon,
  WaitingListIcon,
} from "pianofunclub-shared/components/Other/Icons";
import SearchBar from "pianofunclub-shared/components/Other/SearchBar";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import { useData } from "pianofunclub-shared/providers/DataProvider";
import type {
  BulkUploadNewEnrolmentsMutation,
  BulkUploadNewEnrolmentsMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/BulkUploadNewEnrolmentsMutation.graphql";
import type {
  EditEnrolmentMutation,
  EditEnrolmentMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EditEnrolmentMutation.graphql";
import type {
  EnrolPupilMutation,
  EnrolPupilMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EnrolPupilMutation.graphql";
import type {
  LoadEnrolments_query_enrolments$data,
  LoadEnrolments_query_enrolments$key,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolments_query_enrolments.graphql";
import type { LoadEnrolmentsPaginationQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolmentsPaginationQuery.graphql";
import type { LoadEnrolmentsQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolmentsQuery.graphql";
import { bulk_upload_new_enrolments } from "pianofunclub-shared/relay/graphql/accounts/BulkUploadNewEnrolments";
import { edit_enrolment } from "pianofunclub-shared/relay/graphql/accounts/EditEnrolment";
import { enrol_pupil } from "pianofunclub-shared/relay/graphql/accounts/EnrolPupil";
import {
  load_enrolments,
  load_enrolments_pagination,
} from "pianofunclub-shared/relay/graphql/accounts/LoadEnrolments";
import type { LoadMutationProgressQuery } from "pianofunclub-shared/relay/graphql/general/__generated__/LoadMutationProgressQuery.graphql";
import { load_mutation_progress } from "pianofunclub-shared/relay/graphql/general/LoadMutationProgress";
import {
  ENROLMENT_STATUS,
  INSTRUMENTS,
  LESSON_STAGES,
  SCHOOL_YEARS,
} from "pianofunclub-shared/utils/constants";
import { getScaledWindowDimension, titleCaseConverter } from "pianofunclub-shared/utils/converters";
import { getOrderBy, sortIconExtractor } from "pianofunclub-shared/utils/extractors";
import { useDebounceFunction } from "pianofunclub-shared/utils/hooks";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { State, Action } from "pianofunclub-shared/utils/reducers";
import { waitingListHubTableCsvConverter } from "pianofunclub-shared/utils/tableCsvConverters";

type NavigationProps = WaitingListStackNavigatorProps<"WaitingListHub">;

type RouteProps = WaitingListStackRouteProps<"WaitingListHub">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

const LOAD_X_ENROLMENTS = 50;
const TABLE_BORDER_COLOR = "surface.400";
const TABLE_BORDER_WIDTH = 1;
const TABLE_BORDER_RADIUS = 10;
const FLEX_ARRAY = [0.2, 0.5, 0.6, 0.3, 0.3, 0.3, 0.2, 0.5];

type SelectedEnrolment = {
  enrolmentId: string;
  enrolmentSchoolIsArchived: boolean;
  enrolmentStatus: string | null;
};

export type ShowMutationProgressIndicators = {
  bulkUploadNewEnrolments: boolean;
};

export type ReducerValues = {
  bulkUploadNewEnrolmentsModalIsOpen: boolean;
  changeEnrolmentStatusActionsheetIsOpen?: boolean;
  contentIsRendered: boolean;
  copyDataToClipboard?: () => void;
  dataProvider: DataProvider;
  googleDriveNewBulkEnrolmentSheetUrl: string;
  instrument?: string;
  isRefetching: boolean;
  layoutProvider?: LayoutProvider;
  lessonStageIndex?: string;
  orderBy: string;
  refreshHandler?: () => void;
  schoolId?: string;
  schoolsConnectionId?: string;
  schoolYear?: string;
  searchTerm: string;
  selectedEnrolment?: SelectedEnrolment;
  showCreateSchoolModal: boolean;
  showMutationProgressIndicators: ShowMutationProgressIndicators;
  status?: string;
  subscription?: Subscription;
};

interface ContentProps {
  dispatchState: Dispatch<Action<ReducerValues>>;
  loadEnrolmentsQueryReference: PreloadedQuery<LoadEnrolmentsQuery, Record<string, unknown>>;
  navigation: NavigationProps;
  state: State<ReducerValues>;
}

const WaitingListHubContent: FC<ContentProps> = (props) => {
  const { dispatchState, loadEnrolmentsQueryReference, navigation, state } = props;

  const enrolmentsQueryResponse = usePreloadedQuery(load_enrolments, loadEnrolmentsQueryReference);

  const {
    data: enrolmentsData,
    hasNext,
    isLoadingNext,
    loadNext,
    refetch: refresh,
  } = usePaginationFragment<LoadEnrolmentsPaginationQuery, LoadEnrolments_query_enrolments$key>(
    load_enrolments_pagination,
    enrolmentsQueryResponse
  );

  const [loadMutationProgressQueryReference, loadMutationProgressQuery] =
    useQueryLoader<LoadMutationProgressQuery>(load_mutation_progress);

  const { user } = useAuth();

  useEffect(() => {
    if (user?.profile?.id) {
      loadMutationProgressQuery(
        {
          block: 0, // dummy values (not actually used in query)
          profileId: user.profile.id,
          skipInvoiceTotals: true,
          startingYear: 0, // dummy values (not actually used in query)
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [loadMutationProgressQuery, user?.profile?.id]);

  const [commitEditEnrolment] = useMutation<EditEnrolmentMutation>(edit_enrolment);

  const [commitEnrolPupil, enrolPupilInFlight] = useMutation<EnrolPupilMutation>(enrol_pupil);

  const toast = useToast();

  useEffect(() => {
    if (enrolmentsData.enrolments?.edges && enrolmentsData.enrolments.edges.length > 0) {
      dispatchState({
        input: "dataProvider",
        value: state.values.dataProvider.cloneWithRows([...enrolmentsData.enrolments.edges]),
      });
    } else {
      dispatchState({
        input: "dataProvider",
        value: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
      });
    }

    dispatchState({
      input: "refreshHandler",
      value: () => {
        dispatchState({ input: "contentIsRendered", value: false });
        refresh(
          {
            first: LOAD_X_ENROLMENTS,
            orderBy: state.values.orderBy,
            searchTerm: state.values.searchTerm.trim(),
          },
          {
            fetchPolicy: "network-only",
          }
        );
      },
    });

    dispatchState({
      input: "copyDataToClipboard",
      value: () => {
        Clipboard.setStringAsync(
          waitingListHubTableCsvConverter(enrolmentsData.enrolments?.edges ?? [])
        );
        toast.show({
          render: ({ id }: { id: string }) => (
            <ToastAlert id={id} status="success" title={"Copied Data to Clipboard"} toast={toast} />
          ),
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolmentsData]);

  const onPressEnrolment = useCallback<WaitingListTableRowProps["onPressEnrolment"]>(
    (enrolmentId) => {
      navigation.navigate("Enrolment", {
        enrolmentId: enrolmentId,
        enrolmentsConnectionId: enrolmentsData.enrolments?.__id,
      });
    },
    [enrolmentsData.enrolments?.__id, navigation]
  );

  const onPressSchool = useCallback<WaitingListTableRowProps["onPressSchool"]>(
    (schoolId) => {
      if (schoolId === null || schoolId === undefined) {
        return;
      }

      navigation.navigate("School", {
        schoolId,
      });
    },
    [navigation]
  );

  const onPressStatus = useCallback<WaitingListTableRowProps["onPressStatus"]>(
    (enrolmentId, status, enrolmentSchoolIsArchived) => {
      dispatchState({
        input: "changeEnrolmentStatusActionsheetIsOpen",
        value: true,
      });
      dispatchState({
        input: "selectedEnrolment",
        value: {
          enrolmentId,
          enrolmentSchoolIsArchived,
          enrolmentStatus: status,
        },
      });
    },
    [dispatchState]
  );

  const rowRenderer = useCallback(
    (
      _: unknown,
      data: NonNullable<LoadEnrolments_query_enrolments$data["enrolments"]>["edges"][0],
      index: number
    ) => {
      if (!data?.node) {
        return null;
      }

      return (
        <Box borderColor="surface.400" borderLeftWidth={TABLE_BORDER_WIDTH}>
          <WaitingListTableRow
            cellProps={{
              hideTopBorder: index === 0,
              pressableTextProps: {
                fontFamily: "Poppins-Regular",
              },
              px: 2,
              textProps: {
                fontFamily: "Poppins-Light",
                fontSize: "md",
                textAlign: "center",
              },
            }}
            data={data.node}
            flexArray={FLEX_ARRAY}
            onPressEnrolment={onPressEnrolment}
            onPressSchool={onPressSchool}
            onPressStatus={onPressStatus}
            rowHeight={10}
            tableBorderColor={TABLE_BORDER_COLOR}
            tableBorderWidth={TABLE_BORDER_WIDTH}
          />
        </Box>
      );
    },
    [onPressEnrolment, onPressSchool, onPressStatus]
  );

  const renderFooter = useCallback(() => {
    return isLoadingNext ? (
      <CustomFlatListSpinner
        borderColor={TABLE_BORDER_COLOR}
        borderTopWidth={1}
        position="relative"
        pt="8"
        top="0"
      />
    ) : null;
  }, [isLoadingNext]);

  useEffect(() => {
    dispatchState({ input: "contentIsRendered", value: true });
    // unsubscribe from refetch on unmount
    return () => state.values.subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEnrolmentsQueryReference]);

  const renderCount = useRef(0);
  const environment = useRelayEnvironment();
  const { colors } = useTheme();

  const refetchEnrolments = useCallback(
    (variables: {
      first: number;
      instrument?: string;
      lessonStage?: string;
      orderBy?: string;
      schoolId?: string;
      schoolYear?: string;
      searchTerm?: string;
      status?: string;
    }): Subscription | undefined => {
      dispatchState({ input: "isRefetching", value: true });

      const subscription = fetchQuery<LoadEnrolmentsQuery>(
        environment,
        load_enrolments,
        variables,
        { fetchPolicy: "store-or-network" }
      ).subscribe({
        complete: () => {
          dispatchState({ input: "isRefetching", value: false });
          refresh(variables, { fetchPolicy: "store-only" });
        },
        error: () => {
          dispatchState({ input: "isRefetching", value: false });
        },
      });
      return subscription;
    },
    [dispatchState, environment, refresh]
  );

  const editEnrolment = useCallback<EnrolmentStatusActionsheetProps["editEnrolment"]>(
    (variables) => {
      if (!state.values.selectedEnrolment) {
        return;
      }

      const editEnrolmentConfig = {
        variables: {
          input: {
            ...variables,
            enrolmentId: state.values.selectedEnrolment.enrolmentId,
          },
        },
        onCompleted: (response: EditEnrolmentMutation$data) => {
          if (!response?.editEnrolment?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't update enrolment"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitEditEnrolment(editEnrolmentConfig);
    },
    [commitEditEnrolment, state.values.selectedEnrolment, toast]
  );

  const enrolPupil = useCallback<EnrolmentStatusActionsheetProps["enrolPupil"]>(
    (variables) => {
      if (!state.values.selectedEnrolment) {
        return;
      }

      const enrolPupilConfig = {
        variables: {
          input: {
            enrolmentId: state.values.selectedEnrolment.enrolmentId,
          },
        },
        onCompleted: (response: EnrolPupilMutation$data) => {
          if (response?.enrolPupil?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Successfully enroled pupil"}
                  toast={toast}
                />
              ),
            });

            variables?.onComplete?.();
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Failed to enrol pupil"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitEnrolPupil(enrolPupilConfig);
    },
    [commitEnrolPupil, state.values.selectedEnrolment, toast]
  );

  useEffect(() => {
    if (renderCount.current < 2) {
      renderCount.current += 1;
      return;
    }

    dispatchState({ input: "isRefetching", value: false });
    state.values.subscription?.unsubscribe();

    // fire refetch immediately when orderBy or filters change
    dispatchState({
      input: "subscription",
      value: refetchEnrolments({
        first: LOAD_X_ENROLMENTS,
        instrument: state.values.instrument ?? "",
        lessonStage: state.values.lessonStageIndex ?? "",
        orderBy: state.values.orderBy,
        schoolId: state.values.schoolId,
        schoolYear: state.values.schoolYear ?? undefined,
        searchTerm: state.values.searchTerm.trim(),
        status: state.values.status ?? undefined,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.values.orderBy,
    state.values.schoolId,
    state.values.lessonStageIndex,
    state.values.schoolYear,
    state.values.instrument,
    state.values.status,
  ]);

  useDebounceFunction(
    () => {
      if (renderCount.current < 2) {
        renderCount.current += 1;
        return;
      }

      dispatchState({ input: "isRefetching", value: false });
      state.values.subscription?.unsubscribe();

      // remove any applied sorting when we search (sort by relevance instead)
      // and reapply default sorting if search text is blank
      const orderBy =
        state.values.searchTerm.trim() === "" ? "school__name,positionInWaitingList" : undefined;

      dispatchState({ input: "orderBy", value: orderBy });
      navigation.setParams({ orderBy: orderBy });
      dispatchState({
        input: "subscription",
        value: refetchEnrolments({
          first: LOAD_X_ENROLMENTS,
          instrument: state.values.instrument ?? "",
          lessonStage: state.values.lessonStageIndex ?? "",
          orderBy: orderBy,
          schoolId: state.values.schoolId,
          schoolYear: state.values.schoolYear ?? undefined,
          searchTerm: state.values.searchTerm.trim(),
          status: state.values.status ?? undefined,
        }),
      });
    }, // no delay if clearing text
    state.values.searchTerm !== "" ? 750 : 0,
    [state.values.searchTerm]
  );

  const { scale, width: windowWidth } = useWindowDimensions();

  useEffect(() => {
    // this allows the RLV to resize when the window resizes
    dispatchState({
      input: "layoutProvider",
      value: new LayoutProvider(
        () => "VSEL",
        (type, dim) => {
          switch (type) {
            case "VSEL":
              dim.width = getScaledWindowDimension(windowWidth, scale);
              dim.height = 40;
              break;
            default:
              dim.width = 0;
              dim.height = 0;
          }
        }
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth, scale]);

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(LOAD_X_ENROLMENTS);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  const renderChangeEnrolmentStatusActionsheet = useMemo(() => {
    return (
      <EnrolmentStatusActionsheet
        editEnrolment={editEnrolment}
        enrolmentId={state.values.selectedEnrolment?.enrolmentId}
        enrolmentSchoolIsArchived={
          state.values.selectedEnrolment?.enrolmentSchoolIsArchived ?? false
        }
        enrolmentsConnectionId={enrolmentsData.enrolments?.__id}
        enrolmentStatus={state.values.selectedEnrolment?.enrolmentStatus}
        enrolPupil={enrolPupil}
        enrolPupilInFlight={enrolPupilInFlight}
        isOpen={Boolean(state.values.changeEnrolmentStatusActionsheetIsOpen)}
        onClose={() => {
          dispatchState({
            input: "changeEnrolmentStatusActionsheetIsOpen",
            value: false,
          });
          setTimeout(() => {
            dispatchState({
              input: "selectedEnrolment",
              value: undefined,
            });
          }, 250);
        }}
      />
    );
  }, [
    dispatchState,
    editEnrolment,
    enrolPupil,
    enrolPupilInFlight,
    enrolmentsData.enrolments?.__id,
    state.values.changeEnrolmentStatusActionsheetIsOpen,
    state.values.selectedEnrolment?.enrolmentId,
    state.values.selectedEnrolment?.enrolmentSchoolIsArchived,
    state.values.selectedEnrolment?.enrolmentStatus,
  ]);

  if (!state.values.contentIsRendered) {
    return <LoadingBlobs>Loading Waiting List...</LoadingBlobs>;
  }

  return (
    <>
      <Box flex={1} mx="30px">
        {state.values.dataProvider.getSize() > 0 && state.values.layoutProvider ? (
          <RecyclerListView
            canChangeSize
            dataProvider={state.values.dataProvider}
            layoutProvider={state.values.layoutProvider}
            onEndReached={onEndReached}
            onEndReachedThreshold={2000}
            renderAheadOffset={800}
            renderFooter={renderFooter}
            rowRenderer={rowRenderer}
            scrollViewProps={{
              contentContainerStyle: {
                borderBottomRadius: TABLE_BORDER_RADIUS,
                borderBottomWidth:
                  (enrolmentsData.enrolments?.edges.length ?? 0 > 0) && !isLoadingNext
                    ? TABLE_BORDER_WIDTH
                    : 0,
                borderColor:
                  // @ts-expect-error can't index with variable
                  colors?.[TABLE_BORDER_COLOR.split(".")[0]][TABLE_BORDER_COLOR.split(".")[1]],
                marginBottom: 40,
                overflow: "hidden",
              },
              showsVerticalScrollIndicator: false,
            }}
            style={{
              flex: 1,
              minHeight: 1,
            }}
            suppressBoundedSizeException
            useWindowScroll
          />
        ) : (
          <ListEmptyBanner explainer={"No enrolments found..."}>
            <Text fontSize="6xl">{"😐"}</Text>
          </ListEmptyBanner>
        )}
        {renderChangeEnrolmentStatusActionsheet}
      </Box>
      {loadMutationProgressQueryReference ? (
        <WaitingListMutationProgressIndicators
          dispatchState={dispatchState}
          loadMutationProgressQuery={loadMutationProgressQuery}
          loadMutationProgressQueryReference={loadMutationProgressQueryReference}
          state={state}
        />
      ) : null}
    </>
  );
};

const WaitingListHubScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const { user } = useAuth();

  const { dataState } = useData();

  const toast = useToast();

  const initialState = useMemo(() => {
    return {
      values: {
        bulkUploadNewEnrolmentsModalIsOpen: false,
        contentIsRendered: false,
        dataProvider: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
        googleDriveNewBulkEnrolmentSheetUrl: "",
        isRefetching: false,
        layoutProvider: undefined,
        lessonStageIndex: route.params?.lessonStageIndex,
        orderBy: route.params?.orderBy ?? "school__name,positionInWaitingList",
        parentEmail: route.params?.parentEmail,
        refreshHandler: undefined,
        schoolId: route.params?.schoolId,
        schoolYear: route.params?.schoolYear,
        searchTerm: route.params?.searchTerm ?? "",
        showCreateSchoolModal: false,
        showMutationProgressIndicators: {
          bulkUploadNewEnrolments: false,
        },
        status: route.params?.status ?? "WAITING_LIST_AND_OFFERED",
        subscription: undefined,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadEnrolmentsQueryReference, loadEnrolmentsQuery] =
    useQueryLoader<LoadEnrolmentsQuery>(load_enrolments);

  const [commitBulkUploadNewEnrolments, bulkUploadNewEnrolmentsInFlight] =
    useMutation<BulkUploadNewEnrolmentsMutation>(bulk_upload_new_enrolments);

  useEffect(() => {
    if (!state.values.contentIsRendered) {
      loadEnrolmentsQuery(
        {
          first: LOAD_X_ENROLMENTS,
          instrument: state.values.instrument,
          lessonStage: state.values.lessonStageIndex,
          orderBy: state.values.orderBy,
          schoolId: state.values.schoolId,
          schoolYear: state.values.schoolYear,
          searchTerm: state.values.searchTerm.trim(),
          status: state.values.status,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [
    loadEnrolmentsQuery,
    state.values.contentIsRendered,
    state.values.orderBy,
    state.values.searchTerm,
    state.values.schoolId,
    state.values.lessonStageIndex,
    state.values.schoolYear,
    state.values.instrument,
    state.values.status,
  ]);

  const bulkUploadNewEnrolments = useCallback(
    (variables: { googleDriveNewBulkEnrolmentSheetUrl: string }) => {
      const bulkUploadNewPupilEnrolmentsConfig = {
        variables: {
          input: {
            googleDriveNewBulkEnrolmentsSheetUrl: variables.googleDriveNewBulkEnrolmentSheetUrl,
          },
        },
        onCompleted: (response: BulkUploadNewEnrolmentsMutation$data) => {
          if (response?.bulkUploadNewEnrolments?.success) {
            // refresh on complete
            dispatchState({
              input: "contentIsRendered",
              value: false,
            });
            state.values.refreshHandler?.();

            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Successfuly uploaded enrolments to waiting list"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              duration: null,
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={
                    response?.bulkUploadNewEnrolments?.errors[0].message ??
                    "Please get in touch with one of the team."
                  }
                  id={id}
                  status="error"
                  title={"Failed to upload enrolments to waiting list"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitBulkUploadNewEnrolments(bulkUploadNewPupilEnrolmentsConfig);
      dispatchState({
        input: "showMutationProgressIndicators",
        value: {
          ...state.values.showMutationProgressIndicators,
          bulkUploadNewEnrolments: true,
        },
      });
    },
    [commitBulkUploadNewEnrolments, state.values, toast]
  );

  const bulkUploadNewEnrolmentsHandler = useCallback(
    (googleDriveNewBulkEnrolmentSheetUrl?: string) => {
      if (googleDriveNewBulkEnrolmentSheetUrl || state.values.googleDriveNewBulkEnrolmentSheetUrl) {
        bulkUploadNewEnrolments({
          googleDriveNewBulkEnrolmentSheetUrl:
            googleDriveNewBulkEnrolmentSheetUrl ?? state.values.googleDriveNewBulkEnrolmentSheetUrl,
        });
      }
      dispatchState({
        input: "bulkUploadNewEnrolmentsModalIsOpen",
        value: false,
      });
    },
    [bulkUploadNewEnrolments, state.values.googleDriveNewBulkEnrolmentSheetUrl]
  );

  const inputChangeHandler = useCallback(
    (_: unknown, inputValue?: string) => {
      dispatchState({
        input: "searchTerm",
        value: inputValue,
      });
      navigation.setParams({ searchTerm: inputValue });
    },
    [navigation]
  );

  const inputClearHandler = useCallback(() => {
    if (state.values.searchTerm !== "") {
      dispatchState({
        input: "searchTerm",
        value: "",
      });
      navigation.setParams({ searchTerm: undefined });
    }
  }, [navigation, state.values.searchTerm]);

  const sortHandler = useCallback(
    (column: string) => {
      const orderBy = getOrderBy(column, state.values.orderBy, {
        onlyReverseOrderOfPrimarySort: true,
      });
      dispatchState({
        input: "orderBy",
        value: orderBy,
      });
      navigation.setParams({
        orderBy: orderBy,
      });
    },
    [navigation, state.values.orderBy]
  );

  const tableHeaders = useMemo(() => {
    // these headers are used by all account types
    return [
      {
        data: "Position",
        icon: sortIconExtractor("positionInWaitingList,school__name", state.values.orderBy),
        onPress: () => sortHandler("positionInWaitingList,school__name"),
      },
      {
        data: "Pupil Name",
        icon: sortIconExtractor(
          "-hasInvalidParentPhoneNumber,pupilFirstName,pupilLastName",
          state.values.orderBy
        ),
        onPress: () => sortHandler("-hasInvalidParentPhoneNumber,pupilFirstName,pupilLastName"),
      },
      {
        data: "School",
        icon: sortIconExtractor("school__name,positionInWaitingList", state.values.orderBy),
        onPress: () => sortHandler("school__name,positionInWaitingList"),
      },
      {
        data: "Year Group",
      },
      {
        data: "Class",
      },
      {
        data: "Instrument",
      },
      {
        data: "Stage",
      },
      {
        data: "Status",
      },
    ];
  }, [sortHandler, state.values.orderBy]);

  const schools = useMemo(() => {
    return dataState.values.schools?.edges ?? [];
  }, [dataState.values.schools?.edges]);

  const renderAlerts = useMemo(() => {
    return (
      <>
        <AlertPopup
          alertIsOpen={state.values.bulkUploadNewEnrolmentsModalIsOpen}
          body={
            "Please enter the Google Drive URL of the bulk enrolment Google Sheet below. Ensure that it conforms to the bulk upload template format."
          }
          bodyStyle={{ _text: { fontSize: "md" }, mt: -5 }}
          header="Bulk Upload New Enrolments to Waiting List"
          setAlertIsOpen={() => {
            dispatchState({
              input: "bulkUploadNewEnrolmentsModalIsOpen",
              value: false,
            });
            dispatchState({
              input: "googleDriveNewBulkEnrolmentSheetUrl",
              value: undefined,
            });
          }}
        >
          <VStack alignItems="center" space="8">
            <Text fontSize="md" mt="-4" textAlign="center">
              {"How to prepare the bulk upload in Google Drive?\n"}
              <Text fontFamily="Poppins-Regular">
                {"1. Make a copy of the "}
                <Text
                  color="primary.600"
                  onPress={() =>
                    window.open(
                      "https://docs.google.com/spreadsheets/d/18bBzq38M8--Hf2bsQqZvg9uAPrdsfNXHltbbJjmNRwo/edit#gid=454040445",
                      "_blank"
                    )
                  }
                  textDecorationLine="underline"
                >
                  bulk upload template
                </Text>
                {
                  ".\n2. Fill in the data.\n3. Copy the URL from the address bar into the box below."
                }
              </Text>
            </Text>
            <TextInput
              autoCapitalize="none"
              borderRadius="2xl"
              fontFamily="Poppins-Regular"
              fontSize="md"
              id="googleDriveNewBulkEnrolmentSheetUrl"
              keyboardType="default"
              onFinishEditing={(_, inputValue) => {
                dispatchState({
                  input: "googleDriveNewBulkEnrolmentSheetUrl",
                  value: inputValue,
                });
              }}
              onSubmit={(_, inputValue) => bulkUploadNewEnrolmentsHandler(inputValue)}
              placeholder="Enter Google Drive URL"
              textAlign="center"
            />
            <Button.Group
              alignItems="center"
              flex={1}
              flexDirection="row"
              justifyContent="center"
              mb="4"
              p="0"
              size="lg"
              space={4}
            >
              <ButtonDebounced
                _text={{ fontSize: "lg" }}
                height="50"
                isDisabled={!state.values.googleDriveNewBulkEnrolmentSheetUrl}
                minWidth="56"
                onPress={() => bulkUploadNewEnrolmentsHandler()}
                width="25%"
              >
                Bulk Upload
              </ButtonDebounced>
            </Button.Group>
          </VStack>
        </AlertPopup>
      </>
    );
  }, [
    bulkUploadNewEnrolmentsHandler,
    state.values.bulkUploadNewEnrolmentsModalIsOpen,
    state.values.googleDriveNewBulkEnrolmentSheetUrl,
  ]);

  const renderHeader = useMemo(() => {
    return (
      <VStack mx="30px" pt="6" space="6">
        <HStack ml="1" space="5">
          <WaitingListIcon color="primary.600" size="2xl" />
          <Heading color="primary.600" fontSize="xl" ml="-2">
            Waiting List
          </Heading>
        </HStack>
        <VStack space="4">
          <HStack justifyContent="space-between">
            <VStack space="4">
              <HStack alignItems="center" space="4">
                <Box width="500px">
                  <SearchBar
                    inputClearHandler={inputClearHandler}
                    inputOnChangeHandler={inputChangeHandler}
                    inputSearchHandler={inputChangeHandler}
                    placeholderText={"Search by pupil name, parent email or phone #"}
                    searchText={state.values.searchTerm}
                    showSearchIcon
                  />
                </Box>
                <Select
                  borderRadius="2xl"
                  color={state.values.status ? "surface.900" : "muted.400"}
                  fontSize="md"
                  onValueChange={(itemValue) => {
                    const parsedValue = itemValue !== "ANY" ? itemValue : undefined;
                    dispatchState({
                      input: "status",
                      value: parsedValue,
                    });
                    navigation.setParams({
                      status: parsedValue,
                    });
                  }}
                  placeholder="Select enrolment status"
                  py="2.5"
                  selectedValue={state.values.status ?? "ANY"}
                  width="220px"
                >
                  <Select.Item key={"ANY"} actionSheetLabel={"Any status"} value={"ANY"} />
                  {ENROLMENT_STATUS.map((item) => {
                    return (
                      <Select.Item
                        key={item.value}
                        actionSheetLabel={item.label}
                        startIcon={item.icon}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
              </HStack>
              <HStack alignItems="center" space="4">
                <Select
                  borderRadius="2xl"
                  color={state.values.schoolId ? "surface.900" : "muted.400"}
                  fontSize="md"
                  onValueChange={(itemValue) => {
                    const parsedValue = itemValue !== "ANY" ? itemValue : undefined;
                    dispatchState({
                      input: "schoolId",
                      value: parsedValue,
                    });
                    navigation.setParams({
                      schoolId: parsedValue,
                    });
                  }}
                  placeholder="Select school"
                  py="2.5"
                  selectedValue={state.values.schoolId ?? "ANY"}
                  width="260px"
                >
                  <Select.Item key={"ANY"} actionSheetLabel={"Any school"} value={"ANY"} />
                  {schools.map((item) => {
                    if (!item?.node?.id) {
                      return null;
                    }

                    const isArchived = item.node.archived;

                    return (
                      <Select.Item
                        key={item.node.id}
                        actionSheetLabel={`${titleCaseConverter(item.node.name)} ${
                          isArchived ? "(Archived)" : ""
                        }`}
                        value={item.node.id}
                      />
                    );
                  })}
                </Select>
                <Select
                  bg="surface.100"
                  borderRadius="2xl"
                  color={state.values.lessonStageIndex ? "surface.900" : "muted.400"}
                  fontSize="md"
                  onValueChange={(itemValue) => {
                    dispatchState({
                      input: "lessonStageIndex",
                      value: itemValue,
                    });
                    navigation.setParams({
                      lessonStageIndex: itemValue,
                    });
                  }}
                  placeholder="Select lesson stage"
                  py="2.5"
                  selectedValue={String(state.values.lessonStageIndex ?? "")}
                  width="160px"
                >
                  <Select.Item key={""} actionSheetLabel={"Any stages"} value={""} />
                  {LESSON_STAGES.map((item) => {
                    return (
                      <Select.Item
                        key={item.value}
                        actionSheetLabel={item.label}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
                <Select
                  bg="surface.100"
                  borderRadius="2xl"
                  color={state.values.schoolYear ? "surface.900" : "muted.400"}
                  fontSize="md"
                  onValueChange={(itemValue) => {
                    dispatchState({
                      input: "schoolYear",
                      value: itemValue,
                    });
                    navigation.setParams({
                      schoolYear: itemValue,
                    });
                  }}
                  placeholder="Select school year"
                  py="2.5"
                  selectedValue={state.values.schoolYear?.toString() ?? ""}
                  width="184px"
                >
                  <Select.Item key={""} actionSheetLabel={"Any school year"} value={""} />
                  {SCHOOL_YEARS.map((item) => {
                    return (
                      <Select.Item
                        key={item.label}
                        actionSheetLabel={item.label}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
                <Select
                  bg="surface.100"
                  borderRadius="2xl"
                  color={state.values.instrument ? "surface.900" : "muted.400"}
                  fontSize="md"
                  onValueChange={(itemValue) => {
                    dispatchState({
                      input: "instrument",
                      value: itemValue,
                    });
                    navigation.setParams({
                      instrument: itemValue,
                    });
                  }}
                  placeholder="Select instrument"
                  py="2.5"
                  selectedValue={state.values.instrument ?? ""}
                  width="180px"
                >
                  <Select.Item key={""} actionSheetLabel={"Any instrument"} value={""} />
                  {INSTRUMENTS.map((item) => {
                    return (
                      <Select.Item
                        key={item.label}
                        actionSheetLabel={item.label}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
                <Button
                  bg="surface.400"
                  colorScheme="surface"
                  leftIcon={<RestartIcon size="5" />}
                  onPress={state.values.refreshHandler}
                  p="3"
                />
              </HStack>
            </VStack>
            <VStack alignItems="flex-end" space="4">
              <Button
                _hover={{ bg: "primary.500" }}
                _pressed={{ bg: "primary.600" }}
                _text={{ fontSize: "17" }}
                bg="primary.400"
                leftIcon={<UploadIcon size="lg" />}
                onPress={() => {
                  dispatchState({
                    input: "bulkUploadNewEnrolmentsModalIsOpen",
                    value: true,
                  });
                }}
                px="4"
                shadow={1}
                {...(bulkUploadNewEnrolmentsInFlight ||
                Boolean(
                  typeof user?.profile?.profileGroup?.bulkUploadNewEnrolmentsProgress ===
                    "number" &&
                    user?.profile?.profileGroup?.bulkUploadNewEnrolmentsProgress >= 0 &&
                    user?.profile?.profileGroup?.bulkUploadNewEnrolmentsProgress < 100
                )
                  ? {
                      isLoading: true,
                      isLoadingText: "Uploading Enrolments",
                    }
                  : {})}
              >
                {"Bulk Upload to Waiting List"}
              </Button>
              <Button
                _hover={{ bg: "primary.500" }}
                _pressed={{ bg: "primary.600" }}
                _text={{ fontSize: "17" }}
                bg="primary.400"
                leftIcon={<CopyIcon size="md" />}
                onPress={state.values.copyDataToClipboard}
                px="4"
                shadow={1}
              >
                {"Copy Table to Clipboard"}
              </Button>
            </VStack>
          </HStack>
        </VStack>
        <Box bg="surface.100">
          <Box
            bg="primary.50"
            borderColor={TABLE_BORDER_COLOR}
            borderTopRadius={TABLE_BORDER_RADIUS}
            borderWidth={TABLE_BORDER_WIDTH}
          >
            <Row
              cellProps={{
                hideOuterSideBorder: true,
                hideTopBorder: true,
                px: 2,
                textProps: {
                  fontSize: "md",
                  isTruncated: true,
                  textAlign: "center",
                },
              }}
              data={tableHeaders}
              flexArray={FLEX_ARRAY}
              rowHeight={10}
              rowIndex={0}
              tableBorderColor={TABLE_BORDER_COLOR}
              tableBorderWidth={TABLE_BORDER_WIDTH}
            />
          </Box>
        </Box>
      </VStack>
    );
  }, [
    inputClearHandler,
    inputChangeHandler,
    state.values.searchTerm,
    state.values.status,
    state.values.schoolId,
    state.values.lessonStageIndex,
    state.values.schoolYear,
    state.values.instrument,
    state.values.refreshHandler,
    state.values.copyDataToClipboard,
    schools,
    bulkUploadNewEnrolmentsInFlight,
    user?.profile?.profileGroup?.bulkUploadNewEnrolmentsProgress,
    tableHeaders,
    navigation,
  ]);

  const renderRefetchIndicator = useMemo(() => {
    return state.values.isRefetching ? <CustomFlatListSpinner top="270" /> : null;
  }, [state.values.isRefetching]);

  if (!user?.profile?.id) {
    return null;
  }

  return (
    <Box bg="surface.100" flex={1} pt="70">
      {renderHeader}
      {loadEnrolmentsQueryReference ? (
        <Suspense fallback={<LoadingBlobs>Loading Waiting List...</LoadingBlobs>}>
          <WaitingListHubContent
            dispatchState={dispatchState}
            loadEnrolmentsQueryReference={loadEnrolmentsQueryReference}
            navigation={navigation}
            state={state}
          />
        </Suspense>
      ) : (
        <LoadingBlobs>Loading Waiting List...</LoadingBlobs>
      )}
      {renderAlerts}
      {renderRefetchIndicator}
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Waiting List",
};

export default WaitingListHubScreen;
